import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { DetailSkeleton } from '@/components/PageSkeleton';
import { Empty } from '@/components/ui/empty';
import { ConfirmDelete, DetailRow, ErrorState, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import RenewButton from '@/components/account/RenewButton';
import PaymentInstructions from '@/components/PaymentInstructions';
import { ORDER_STATUS_STYLES, PAYMENT_KIND_STYLES } from '@/shared/constants';
import { ORDER_FIELDS, PAYMENT_FIELDS } from '@/shared/fields';
import { TELLY, money, orderTotal, paymentLabel, planSummary, tellyCount, timeLeft } from '@/shared/pricing';
import { committed, rows } from '@/shared/data';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';
import { useT, translate } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';
import { B } from '@/shared/locales/account2/keys';
import { C } from '@/shared/locales/checkout/keys';
import { planSummaryT, timeLeftT } from '@/shared/accountText';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/account/orders/:id', layout: 'owner', access: "profile === 'Customer_c'" };

async function copy(value, label) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(translate(B.labelCopied, { label }));
  } catch {
    toast.error(translate(B.cantCopyIt));
  }
}

function Secret({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-sm">{value}</p>
      </div>
      <Button variant="ghost" size="icon" aria-label={`Copy ${label}`} onClick={() => copy(value, label)}>
        <ApperIcon name="Copy" size={16} />
      </Button>
    </div>
  );
}

export default function OrderDetail() {
  const { t } = useT();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: order, loading, error, run } = useFetch(
    () => sdk.table('orders_c').select(ORDER_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );
  const payments = useFetch(
    () => sdk.table('payments_c').select(PAYMENT_FIELDS).where(eq('order_c', id)).orderByDesc('paid_on_c').limit(20).fetch().then(rows),
    [id],
  );

  const back = (
    <Button variant="ghost" size="sm" asChild className="mb-4 -ms-2">
      <Link to="/account/orders">
        <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
        {t(B.backOrders)}
      </Link>
    </Button>
  );

  if (loading && order === undefined) {
    return (
      <div className="mx-auto max-w-5xl">
        {back}
        <DetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl">
        {back}
        <ErrorState message={error} onRetry={run} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-5xl">
        {back}
        <Empty icon="Ghost" title={t(B.notFound)} description={t(B.notFoundP)} />
      </div>
    );
  }

  const licences = tellyCount(order);
  const hasLine = order.plan_c !== 'Telly';
  const pending = order.status_c === 'Pending';
  const removable = pending || order.status_c === 'Cancelled';
  const payRows = payments.data ?? [];

  async function cancel() {
    const res = await sdk.table('orders_c').update({ Id: order.Id, status_c: 'Cancelled' });
    if (!committed(res, t(B.cancelled))) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('orders_c').remove(order.Id);
    if (!committed(res, t(B.deleted))) return false;
    navigate('/account/orders');
    return true;
  }

  return (
    <div className="mx-auto max-w-5xl">
      {back}
      <PageHeader
        eyebrow={t(B.orderedOn, { date: formatLocalDate(order.CreatedOn, DATE_FORMATS.SHORT) })}
        title={<span className="font-mono tracking-wide">{order.Name}</span>}
        description={planSummaryT(order, t)}
        actions={
          <>
            <StatusBadge value={order.status_c} styles={ORDER_STATUS_STYLES} />
            <RenewButton order={order} onDone={run} />
            {pending && (
              <ConfirmAction
                label={t(B.cancelOrder)}
                icon="Ban"
                title={t(B.cancelQ)}
                description={t(B.cancelP)}
                busyLabel={t(B.cancelling)}
                destructive
                onConfirm={cancel}
              />
            )}
            {removable && (
              <ConfirmDelete
                title={t(B.deleteQ)}
                description={t(B.deleteP, { code: order.Name })}
                onConfirm={remove}
              />
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {hasLine && (
          <Panel title={t(B.yourLine)} icon="Radio" bodyClassName="space-y-2 py-4">
            {order.line_username_c ? (
              <>
                {order.line_server_c && <Secret label={t(B.server)} value={order.line_server_c} />}
                <Secret label={t(B.username)} value={order.line_username_c} />
                {order.line_password_c && <Secret label={t(B.password)} value={order.line_password_c} />}
                {order.include_epg_c && (
                  <p className="flex items-center gap-2 pt-1 text-sm text-highlight">
                    <ApperIcon name="CalendarRange" size={16} />
                    {t(B.epgIncl)}
                  </p>
                )}
                {order.line_note_c && <p className="pt-1 text-sm text-muted-foreground">{order.line_note_c}</p>}
                {order.credentials_sent_at_c && (
                  <p className="text-xs text-muted-foreground">{t(B.sentWhen, { when: formatRelativeTime(order.credentials_sent_at_c) })}</p>
                )}
              </>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                {t(B.lineSoon)}
              </p>
            )}
          </Panel>
        )}

        {licences > 0 && (
          <Panel title={`${TELLY.name} · ${t('{count} licences', { count: licences })}`} icon="MonitorPlay" bodyClassName="space-y-3 py-4">
            {order.telly_key_c ? (
              <>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 font-mono text-sm">
                  {order.telly_key_c}
                </pre>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => copy(order.telly_key_c, t(B.licenceKey))}>
                    <ApperIcon name="Copy" size={16} />
                    {t(B.copyKey)}
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={TELLY.url} target="_blank" rel="noreferrer">
                      <ApperIcon name="Download" size={16} />
                      {t(B.getPlayer)}
                    </a>
                  </Button>
                </div>
              </>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                {t(B.keySoon)}
              </p>
            )}
          </Panel>
        )}

        <Panel title={t(B.billing)} icon="Wallet">
          <dl className="divide-y divide-border">
            <DetailRow label={t(B.price)}>{money(order.price_c)}</DetailRow>
            <DetailRow label={t(B.discountL)}>
              {Number(order.discount_c) > 0 ? `−${money(order.discount_c)} (${order.discount_code_c})` : null}
            </DetailRow>
            <DetailRow label={t(C.total)}>
              <span className="font-medium text-primary">{money(orderTotal(order))}</span>
            </DetailRow>
            <DetailRow label={t(C.payMethod)}>{t(paymentLabel(order.payment_method_c))}</DetailRow>
            <DetailRow label={t(B.noRefund)}>
              {order.refund_accepted_at_c ? t(B.acceptedOn, { date: formatLocalDate(order.refund_accepted_at_c, DATE_FORMATS.SHORT) }) : null}
            </DetailRow>
          </dl>
          {(order.status_c === 'Pending' || order.status_c === 'Invoiced') && (
            <PaymentInstructions
              method={order.payment_method_c}
              amount={orderTotal(order)}
              reference={order.Name}
              className="mb-4 mt-2"
            />
          )}
        </Panel>

        <Panel title={t(B.dates)} icon="CalendarClock">
          <dl className="divide-y divide-border">
            <DetailRow label={t(B.colOrdered)}>{formatLocalDate(order.CreatedOn, DATE_FORMATS.SHORT)}</DetailRow>
            <DetailRow label={t(B.activated)}>
              {order.activated_at_c ? formatLocalDate(order.activated_at_c, DATE_FORMATS.SHORT) : null}
            </DetailRow>
            <DetailRow label={t(B.colEnds)}>
              {order.ends_at_c ? `${formatLocalDate(order.ends_at_c, DATE_FORMATS.SHORT)} · ${timeLeftT(order.ends_at_c, t)}` : null}
            </DetailRow>
            <DetailRow label={t(B.renewalReq)}>
              {order.renew_requested_at_c ? formatRelativeTime(order.renew_requested_at_c) : null}
            </DetailRow>
          </dl>
        </Panel>

        <Panel title={t(B.orderDetails)} icon="ClipboardList">
          <dl className="divide-y divide-border">
            <DetailRow label={t(B.name)}>{order.full_name_c}</DetailRow>
            <DetailRow label={t(B.email)}>{order.email_c}</DetailRow>
            <DetailRow label={t(B.contact)}>
              {order.contact_handle_c ? `${order.contact_method_c} · ${order.contact_handle_c}` : null}
            </DetailRow>
            <DetailRow label={t(B.device)}>{order.device_c}</DetailRow>
            {hasLine && <DetailRow label={t(B.adult)}>{order.adult_content_c ? t(B.included) : t(B.notIncluded)}</DetailRow>}
            {hasLine && <DetailRow label={t(B.freeTrial)}>{order.wants_trial_c ? t(B.requested) : t('No')}</DetailRow>}
            <DetailRow label={t(B.notesL)}>{order.notes_c}</DetailRow>
          </dl>
        </Panel>

        <Panel
          title={t(B.payments)}
          icon="Receipt"
          actions={
            <Button variant="ghost" size="sm" asChild>
              <Link to="/account/receipts">{t(A.navReceipts)}</Link>
            </Button>
          }
        >
          {payments.loading && payments.data === undefined ? (
            <p className="py-4 text-sm text-muted-foreground">{t(B.loadingPay)}</p>
          ) : payments.error ? (
            <ErrorState message={payments.error} onRetry={payments.run} />
          ) : payRows.length ? (
            <ul className="divide-y divide-border">
              {payRows.map((p) => (
                <li key={p.Id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0">
                    <span className="block font-mono">{p.Name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {formatLocalDate(p.paid_on_c, DATE_FORMATS.SHORT)} · {t(paymentLabel(p.method_c))}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge value={p.kind_c} styles={PAYMENT_KIND_STYLES} />
                    <span className="tabular-nums">{money(p.amount_c)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-4 text-sm text-muted-foreground">{t(B.noPay)}</p>
          )}
        </Panel>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button variant="outline" asChild>
          <Link to={`/account/tickets?order=${order.Id}`}>
            <ApperIcon name="LifeBuoy" size={16} />
            {t(B.helpOrder)}
          </Link>
        </Button>
        {['Active', 'Trial'].includes(order.status_c) && (
          <Button variant="outline" asChild>
            <Link to={`/account/reviews?order=${order.Id}`}>
              <ApperIcon name="Star" size={16} />
              {t(B.writeReview)}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
